import React, { useEffect, useState } from 'react';
import './styles';
import { useSelector } from 'react-redux';
import {
	OPTION_ADDITIONAL_INFO_TYPES_DROPDOWN,
	OPTION_EDITOR_CERTIFICATIONS_DROPDOWN_VALUES,
	OPTION_PHOTOGRAPHER_CERTIFICATIONS_DROPDOWN_VALUES,
	OPTION_TYPES_DROPDOWN_VALUES,
	PACKAGE_TYPES_DROPDOWN_VALUES
} from '../../../../../../utils/Config/Constants';
import UILabeledInput from '../../../../../../components/UI/UILabeledInput';
import UIListInputRow from '../../../../../../components/UI/UIListInputRow';
import UIButton from '../../../../../../components/UI/UIButton/UIButton';
import UIRegionRow from '../../../../../../components/UI/UIRegionRow/UIRegionRow';
import Queries from '../../../../../../utils/API/Queries';
import UIOptionRow from '../../../../../../components/UI/UIOptionRow/UIOptionRow';
import UIStepOverlayCard from '../../../../../../components/UI/UIStepOverlayCard';
import { OVERLAY_CLOSE_TYPES } from '../../../../../../components/UI/UIOverlayCard';
import {
	UI_LABELED_INPUT_CURRENCY_TYPE,
	UI_LABELED_INPUT_DROPDOWN_TYPE,
	UI_LABELED_INPUT_NUMBER_TYPE,
	UI_LABELED_INPUT_TEXT_FIELD
} from '../../../../../../components/UI/UILabeledInput/config';
import { CreateOptionProps, RequiredInfoType } from './types';
import { SECTIONS } from '../config';
import { getCurrentUserSelector, getSelectedRegionSelector, getSignedInUserSelector } from '../../../../../../redux/selectors/CurrentUserSelectors';
import { setSelectedRegion } from '../../../../../../redux/store/user/actions';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/react-hooks';
import { DropdownItem } from '../../../../../../components/UI/UILabeledInput/types';
import * as strings from './strings';

export default function CreateOption({ goBack, onFinish }: CreateOptionProps) {
	const selectedRegion = useSelector(getSelectedRegionSelector);
	const signedInUser = useSelector(getSignedInUserSelector);
	const currentUser = useSelector(getCurrentUserSelector);

	const [steps, setSteps] = useState([SECTIONS.info, SECTIONS.technicianInfo, SECTIONS.editorInfo, SECTIONS.regions, SECTIONS.adminInfo]);
	const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
	const [photographerCertificatesRequired, setPhotographerCertificatesRequired] = useState<string[]>([]);
	const [editorCertificatesRequired, setEditorCertificatesRequired] = useState<string[]>([]);
	const [additionalOptions, setAdditionalOptions] = useState<AdditionalOptionType[]>([]);
	const [regions, setRegions] = useState<RegionType[]>([]);
	const [quantity, setQuantity] = useState<number>();
	const [requiredInfo, setRequiredInfo] = useState<RequiredInfoType | undefined>(undefined);

	const [editorInstructions, setEditorInstructions] = useState<string>('');
	const [optionName, setOptionName] = useState<string>('');
	const [optionPrice, setOptionPrice] = useState<number | undefined>(undefined);
	const [finalFileCount, setFinalFileCount] = useState<string>('');
	const [optionDescription, setOptionDescription] = useState<string>('');
	const [optionType, setOptionType] = useState<DropdownItem>();
	const [packageType, setPackageType] = useState<DropdownItem>();

	const [technicianPay, setTechnicianPay] = useState<number>();
	const [photographerFileCount, setPhotographerFileCount] = useState<string>();
	const [payTime, setPayTime] = useState<number | undefined>();
	const [photographerInstructions, setPhotographerInstructions] = useState<string>('');
	const [duration, setDuration] = useState<number>();

	const [isComplete, setIsComplete] = useState<boolean>(false);
	const [isCreatingOption, setIsCreatingOption] = useState<boolean>(false);
	const [requiredInfoPrompt, setRequiredInfoPrompt] = useState<string>('');
	const [selectedOption, setSelectedOption] = useState<OptionType>();
	const [createNewOption] = useMutation(Queries.CREATE_OPTION);

	useEffect(() => {
		(async () => {
			setSteps([SECTIONS.info, SECTIONS.technicianInfo, SECTIONS.editorInfo, SECTIONS.regions, SECTIONS.adminInfo]);
		})();
	}, []);

	// Mark: Component functions
	const createOption = async (isComplete) => {
		if (!isComplete) {
			toast.error(strings.createOptionError);
			return;
		}
		setIsCreatingOption(true);

		let priceParsed = optionPrice && optionPrice * 100;
		let techPayParsed = technicianPay && technicianPay * 100;
		let variables = {
			name: optionName,
			price: priceParsed,
			finalFileCount,
			description: optionDescription,
			type: optionType,
			quantity,
			packageType: packageType,
			photographerPay: techPayParsed,
			photographerFileCount,
			payTime,
			photographerInstructions,
			duration,
			photographerCertificatesRequired,
			editorInstructions,
			editorCertificatesRequired,
			regions: regions.map((region) => region.id),
			requiredInfo: requiredInfo,
			requiredInfoPrompt,
			additionalOptions: additionalOptions.map((option) => option.id)
		};
		const response = await createNewOption({ variables }).catch((error) => {
			toast.error(error.message);
			setIsCreatingOption(false);
		});

		const { createOption } = response['data'];
		if (createOption) {
			toast.success(strings.createOptionSuccess);
			goBack();
			onFinish(createOption);
		} else {
			setIsCreatingOption(false);
			toast.error('Error!');
		}
	};

	const getAvailableEditorCerts = () => {
		let array = OPTION_EDITOR_CERTIFICATIONS_DROPDOWN_VALUES;
		array = array.filter((x) => !editorCertificatesRequired.includes(x.value));
		return array;
	};

	const getAvailablePhotographerCerts = () => {
		let array = OPTION_PHOTOGRAPHER_CERTIFICATIONS_DROPDOWN_VALUES;
		array = array.filter((x) => !photographerCertificatesRequired.includes(x.value));
		return array;
	};

	const isSectionComplete = () => {
		const currentStep = steps[currentStepIndex];
		switch (currentStep) {
			case SECTIONS.info:
				const infoRequirementsSet = new Set([optionName, optionPrice, finalFileCount, optionDescription, optionType, quantity, packageType]);
				return !infoRequirementsSet.has('') && !infoRequirementsSet.has(undefined);
			case SECTIONS.technicianInfo:
				return true;
			case SECTIONS.editorInfo:
				return editorInstructions !== '' && editorInstructions !== undefined;
			case SECTIONS.regions:
				return true;
			case SECTIONS.adminInfo:
				return requiredInfo !== undefined;
			default:
				return false;
		}
	};

	//
	const getCardTitle = () => {
		switch (steps[currentStepIndex]) {
			case SECTIONS.info:
				return strings.cardTitleInfo;
			case SECTIONS.technicianInfo:
				return strings.cardTitleTechInfo;
			case SECTIONS.editorInfo:
				return strings.cardTitleEditorInfo;
			case SECTIONS.regions:
				return strings.cardTitleRegion;
			case SECTIONS.adminInfo:
				return strings.cardTitleAdmin;
			default:
				return;
		}
	};

	const renderCurrentStep = (selectedStep) => {
		switch (selectedStep) {
			case SECTIONS.info:
				return renderInfo();
			case SECTIONS.technicianInfo:
				return renderTechnicianInfo();
			case SECTIONS.editorInfo:
				return renderEditorInfo();
			case SECTIONS.regions:
				return renderRegions();
			case SECTIONS.adminInfo:
				return renderAdminInfo();
			default:
				return;
		}
	};

	//Mark: Render functions

	const renderInfo = () => {
		return (
			<div className='createOption__section'>
				<div>
					<UIListInputRow label={strings.optionNameLabel} placeholder={strings.optionNamePlaceholder} value={optionName} onChangeValue={setOptionName} />
					<UIListInputRow
						type={UI_LABELED_INPUT_CURRENCY_TYPE}
						label={strings.optionPriceLabel}
						placeholder={strings.optionPricePlaceholder}
						value={optionPrice}
						onChangeNumberValue={setOptionPrice}
					/>
					<UIListInputRow
						type={UI_LABELED_INPUT_NUMBER_TYPE}
						label={strings.optionImageCountLabel}
						placeholder={strings.optionImageCountPlaceholder}
						value={finalFileCount}
						onChangeValue={setFinalFileCount}
					/>
					<UIListInputRow
						type={UI_LABELED_INPUT_TEXT_FIELD}
						label={strings.optionDescriptionLabel}
						placeholder={strings.optionDescriptionPlaceholder}
						value={optionDescription}
						onChangeValue={setOptionDescription}
					/>
					<UIListInputRow
						type={UI_LABELED_INPUT_DROPDOWN_TYPE}
						label={strings.optionTypeLabel}
						options={OPTION_TYPES_DROPDOWN_VALUES}
						value={optionType}
						onChangeDropdownValue={(optionType) => setOptionType(optionType.value)}
					/>
					<UIListInputRow
						type={UI_LABELED_INPUT_NUMBER_TYPE}
						label={strings.quantityLabel}
						placeholder={strings.quantityQuantity}
						value={quantity}
						onChangeNumberValue={setQuantity}
					/>
					<UIListInputRow
						type={UI_LABELED_INPUT_DROPDOWN_TYPE}
						label={strings.packageTypeLabel}
						options={PACKAGE_TYPES_DROPDOWN_VALUES}
						value={packageType}
						onChangeDropdownValue={(packageType) => setPackageType(packageType.value)}
					/>
				</div>
			</div>
		);
	};

	const renderTechnicianInfo = () => {
		return (
			<div className='createOption__section'>
				<UIListInputRow
					type={UI_LABELED_INPUT_CURRENCY_TYPE}
					label={strings.technicianPayLabel}
					placeholder={strings.technicianPayPlaceholder}
					value={technicianPay}
					onChangeNumberValue={setTechnicianPay}
				/>

				<UIListInputRow
					type={UI_LABELED_INPUT_NUMBER_TYPE}
					label={strings.photographerFileCountLabel}
					placeholder={strings.photographerFileCountPlaceholder}
					value={photographerFileCount}
					onChangeValue={setPhotographerFileCount}
				/>
				<UIListInputRow
					type={UI_LABELED_INPUT_NUMBER_TYPE}
					label={strings.payTimeLabel}
					placeholder={strings.payTimePlaceholder}
					value={payTime}
					onChangeNumberValue={setPayTime}
				/>
				<UIListInputRow
					type={UI_LABELED_INPUT_TEXT_FIELD}
					label={strings.photographerInstructionsLabel}
					placeholder={strings.photographerInstructionsPlaceholder}
					value={photographerInstructions}
					onChangeValue={setPhotographerInstructions}
				/>
				{((optionType && optionType.value === 'droneVideo') || (optionType && optionType.value === 'video')) && (
					<UIListInputRow
						type={UI_LABELED_INPUT_NUMBER_TYPE}
						label={strings.durationLabel}
						placeholder={strings.durationPlaceholder}
						value={duration}
						onChangeNumberValue={setDuration}
					/>
				)}
				<UIListInputRow
					type={UI_LABELED_INPUT_DROPDOWN_TYPE}
					label={strings.certTechLabel}
					placeholder={strings.certTechPlaceholder}
					options={getAvailablePhotographerCerts()}
					value={{ value: undefined, label: 'add a certification' }}
					onChangeDropdownValue={(option) => setPhotographerCertificatesRequired([...photographerCertificatesRequired, option.value])}>
					<div className='createOption__section--certificates'>
						{photographerCertificatesRequired.length > 0 ? (
							photographerCertificatesRequired.map((certification) => {
								return (
									<p
										key={certification}
										className='optionCertification'
										onClick={() =>
											setPhotographerCertificatesRequired(photographerCertificatesRequired.filter((certificate) => certificate !== certification))
										}>
										{certification}
									</p>
								);
							})
						) : (
							<p className='general-sub-text noAdditionalOptionsText__alt'>{strings.noOptionText}</p>
						)}
					</div>
				</UIListInputRow>
			</div>
		);
	};

	const renderEditorInfo = () => {
		return (
			<div className='createOption__section'>
				<UIListInputRow
					type={UI_LABELED_INPUT_TEXT_FIELD}
					label={strings.editorInstructionsLabel}
					placeholder={strings.editorInstructionsPlaceholder}
					value={editorInstructions}
					onChangeValue={setEditorInstructions}
				/>
				<UIListInputRow
					type={UI_LABELED_INPUT_DROPDOWN_TYPE}
					label={strings.addCertLabel}
					placeholder={strings.addCertPlaceholder}
					options={getAvailableEditorCerts()}
					value={{ value: undefined, label: 'add a certification' }}
					onChangeDropdownValue={(option) => setEditorCertificatesRequired([...editorCertificatesRequired, option.value])}>
					<div className='createOption__section--certificates'>
						{editorCertificatesRequired.length > 0 ? (
							editorCertificatesRequired.map((certification) => {
								return (
									<p
										key={certification}
										className='optionCertification'
										onClick={() => setEditorCertificatesRequired(editorCertificatesRequired.filter((certificate) => certificate !== certification))}>
										{certification}
									</p>
								);
							})
						) : (
							<p className='general-sub-text'>{strings.noEditorCertText}</p>
						)}
					</div>
				</UIListInputRow>
			</div>
		);
	};

	const renderRegions = () => {
		let optionsDropdown = signedInUser.regions
			.filter((region) => regions.filter((innerRegion) => innerRegion.id === region.id).length === 0)
			.map((region) => {
				return { label: region.name, value: region };
			});

		const removeRegion = async (region) => {
			let newRegions = regions.filter((regionCheck) => region.id !== regionCheck.id);
			setRegions(newRegions);
		};

		return (
			<div className='createOption__section'>
				<UIListInputRow
					key={optionsDropdown.length}
					label={strings.optionsDropdownLabel}
					type={UI_LABELED_INPUT_DROPDOWN_TYPE}
					options={optionsDropdown}
					containerStyle={{ width: 320 }}
					style={{ width: '100%' }}
					value={{ value: undefined, label: strings.addRegionPlaceholder }}
					noOptionsMessage={strings.optionsDropdownMessage}
					onChangeDropdownValue={(value) => setRegions([...regions, value.value])}
					placeholder={strings.addRegionPlaceholder}
				/>
				<div className='createOption--region'>
					{regions.length > 0 &&
						regions.map((region) => {
							return (
								<div
									key={region.id}
									onClick={async (e) => {
										e.stopPropagation();
									}}>
									<UIRegionRow region={region} onRemove={() => removeRegion(region)} />
								</div>
							);
						})}
				</div>
			</div>
		);
	};

	const renderAdminInfo = () => {
		let regions = currentUser.regions.map((region) => {
			return { label: region.name, value: region };
		});
		let regionOptions: DropdownItem[] = [];
		if (selectedRegion) {
			let addOnOptions = selectedRegion.options.filter((option) => additionalOptions.map((option) => option.id).indexOf(option.id) === -1);
			for (let i = 0; i < addOnOptions.length; i++) {
				let option = addOnOptions[i];
				regionOptions.push({
					value: option,
					label: option.name + '  ($' + option.price / 100 + ')'
				});
			}
		}

		return (
			<div className='createOption__section'>
				<UIListInputRow
					type={UI_LABELED_INPUT_DROPDOWN_TYPE}
					placeholder={strings.requiredInfoPlaceholder}
					label={strings.requiredInfoLabel}
					options={OPTION_ADDITIONAL_INFO_TYPES_DROPDOWN}
					value={requiredInfo}
					onChangeDropdownValue={(requiredInfo: DropdownItem) => {
						setRequiredInfo(requiredInfo.value);
						setIsComplete(true);
					}}
				/>
				{requiredInfo ? (
					requiredInfo.value === 'text' ? (
						<UIListInputRow
							placeholder={strings.requiredInfoPromptPlaceholder}
							type={UI_LABELED_INPUT_TEXT_FIELD}
							label={strings.requiredInfoPromptLabel}
							value={requiredInfoPrompt}
							onChangeValue={setRequiredInfoPrompt}
						/>
					) : undefined
				) : undefined}
				<div className='createOption__additionalOptions'>
					<p className='createOption__additionalOptions--title'>Assign an additional option</p>
					<UILabeledInput
						key={selectedRegion.id}
						type={UI_LABELED_INPUT_DROPDOWN_TYPE}
						label={strings.regionLabel}
						containerStyle={{ marginTop: 16, width: 320 }}
						value={{ value: selectedRegion, label: selectedRegion.name }}
						options={regions}
						onChangeDropdownValue={() => setSelectedRegion(selectedRegion)}
						placeholder={strings.regionPlaceholder}
					/>
					{regionOptions.length > 0 ? (
						<UILabeledInput
							key={JSON.stringify(selectedOption)}
							type={UI_LABELED_INPUT_DROPDOWN_TYPE}
							label={strings.selectOptionLabel}
							containerStyle={{ marginTop: 8, width: 320 }}
							value={selectedOption ? { value: selectedOption, label: selectedOption.name } : undefined}
							options={regionOptions}
							onChangeDropdownValue={(value) => {
								setSelectedOption(value.value);
							}}
							placeholder={strings.selectOptionPlaceholder}
						/>
					) : undefined}
					<UIButton
						type={UIButton.FILL_TYPE}
						className='assignButton'
						text={strings.assignButton}
						isDisabled={selectedOption === undefined}
						onClick={() => {
							if (selectedOption) {
								setSelectedOption(undefined);
								setAdditionalOptions([...additionalOptions, selectedOption]);
							}
						}}
					/>
					{additionalOptions ? (
						additionalOptions.length > 0 ? (
							additionalOptions.map((optionToRemove) => {
								return (
									<UIOptionRow
										onClick={() => setAdditionalOptions(additionalOptions.filter((additionalOption) => additionalOption.id !== optionToRemove.id))}
										option={optionToRemove}
									/>
								);
							})
						) : (
							<p className='noAdditionalOptionsText'>{strings.noAdditionalOptionsText}</p>
						)
					) : (
						<p className='noAdditionalOptionsText__alt'>{strings.noAdditionalOptionsTextAlt}</p>
					)}
				</div>
			</div>
		);
	};

	const goNextStep = async () => {
		if (currentStepIndex === steps.length - 1) {
			await createOption(isComplete);
			return;
		}
		setCurrentStepIndex(currentStepIndex + 1);
	};
	const goBackStep = () => {
		if (currentStepIndex === 0) {
			goBack();
		} else {
			setCurrentStepIndex(currentStepIndex - 1);
		}
	};

	return (
		<UIStepOverlayCard
			currentStep={currentStepIndex}
			steps={steps}
			canGoToNextStep={isSectionComplete()}
			goToNextStep={goNextStep}
			hideModal={goBackStep}
			className='createOption'
			disableLastStep={false}
			contentClassName={'createOption--cardContent'}
			closeType={currentStepIndex === 0 ? OVERLAY_CLOSE_TYPES.close : OVERLAY_CLOSE_TYPES.back}
			title={getCardTitle()}
			onClick={(e) => e.stopPropagation()}>
			{renderCurrentStep}
		</UIStepOverlayCard>
	);
}
